import type { HtmxResponseEvent } from "../global";
import htmx from "htmx.org";
import * as Sentry from "@sentry/browser";
import { OfficeDownload } from "../office/OfficeDownload.ts";
import { dispatch } from "../utils/events.ts";
import { getCsrfHeaderName, getCsrfHeaderValue } from "../ext/htmxAntiCsrf.ts";
import { default as tlConfig } from "../config/termlynx"

async function retry(e: HtmxResponseEvent) {
  const config = e.detail.requestConfig;
  const options = {
    source: config.elt,
    target: config.target,
    swap: config.swapStyle,
    values: config.parameters,
  };
  await htmx.ajax(config.verb, config.path, options);
}

async function reupload(uploadUrl: string, signal?: AbortSignal) {
  const download = new OfficeDownload(Office.context);
  const file = await download.start(signal);
  const formData = new FormData();
  formData.append("docs", file, file.name);
  const headers: HeadersInit = {};
  headers[getCsrfHeaderName()] = getCsrfHeaderValue();

  return await fetch(uploadUrl, {
    method: "POST",
    headers,
    body: formData,
  });
}

async function handleMissingDocument(e: HtmxResponseEvent) {
  const abortController = new AbortController();
  dispatch("document-download-started", { signal: abortController.signal });
  const uploadUrl = e.detail.xhr.getResponseHeader("TL-Upload-Url")!;
  const errorUrl = e.detail.xhr.getResponseHeader("TL-Error-Url")!;

  try {
    const response = await reupload(uploadUrl, abortController.signal);

    if (response.status !== 200) {
      const uploadError = new Error(`Document reupload failed with status ${response.status}`);
      console.error(uploadError);
      if (Sentry.isInitialized()) Sentry.captureException(uploadError, { extra: { uploadUrl, status: response.status } });
      window.location.assign(errorUrl);
      return;
    }

    await retry(e);
    dispatch("document-upload-completed", "retry");
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      console.log("Retry download was aborted");
      dispatch("document-download-aborted");
      return;
    }
    console.error("Unexpected error during document re-upload:", error);
    if (Sentry.isInitialized()) Sentry.captureException(error);
    throw error;
  }
}

async function handleMissingAccessToken(e: HtmxResponseEvent) {
  const refreshUrl = e.detail.xhr.getResponseHeader("TL-Refresh-Url")!;
  const loginUrl = e.detail.xhr.getResponseHeader("TL-Login-Url")!;
  const headers: HeadersInit = {};
  headers[getCsrfHeaderName()] = getCsrfHeaderValue();
  const refreshResponse = await fetch(refreshUrl, { headers });
  if (refreshResponse.status !== 200) {
    window.location.assign(loginUrl);
    return;
  }

  await retry(e);
}

async function handleHtmxResponseError(e: HtmxResponseEvent) {
  const { xhr } = e.detail;
  if (xhr.status === 412) {
    await handleMissingDocument(e);
    return;
  }

  if (xhr.status === 413) {
    window.location.assign(tlConfig.tooLargeUrl);
    return;
  }

  if (xhr.status === 428) {
    await handleMissingAccessToken(e);
    return;
  }

  const error = new Error(`Unhandled HTMX response error: ${xhr.status} ${xhr.statusText}`);
  console.error(error, { url: e.detail.requestConfig.path });
  if (Sentry.isInitialized()) Sentry.captureException(error, { extra: { status: xhr.status, url: e.detail.requestConfig.path } });
}

document.addEventListener("htmx:responseError", handleHtmxResponseError);
